import * as React from 'react';
import { observer } from 'mobx-react';
import { Select } from '@payscale/design';
import { EntityTypes, EventTypes } from 'typings/enums';
import { ManagerWorkflowStore } from 'nomst/Stores/Decision/managerWorkflow.store';
import { ManagerWorkflow } from 'nomst/Stores/Decision/managerWorkflow';
import { IWorkflowReviewer, IWorkflowEvent } from 'typings/workflow';
import { User } from 'nomst/Stores/User/user';
import './workflowSupportHeader.scss';

export interface IWorkflowSupportHeaderProps {
    managerWorkflowStore: ManagerWorkflowStore;
    user: User;
}

export interface IWorkflowSupportHeaderState {
    selectedReviewerIndex: number;
    oldWorkflow?: ManagerWorkflow;
    employeeNameMap: Map<string, string>;
}

const areEqual = (reviewer1: Nullable<IWorkflowReviewer>, reviewer2: Nullable<IWorkflowReviewer>) => {
    if (reviewer1 == null && reviewer2 == null) {
        return true;
    }
    return (reviewer1
        && reviewer2
        && reviewer1.reviewerId === reviewer2.reviewerId
        && reviewer1.workflowReviewerType === reviewer2.workflowReviewerType
        && reviewer1.workflowRuleName === reviewer2.workflowRuleName);
};

const areDifferent = (oldReviewers: IWorkflowReviewer[], newReviewers: IWorkflowReviewer[]) => {
    if (oldReviewers.length !== newReviewers.length) {
        return true;
    }

    for (let i = 0; i < oldReviewers.length; i++) {
        if (!areEqual(oldReviewers[i], newReviewers[i])) {
            return true;
        }
    }
    return false;
};

export class WorkflowSupportHeader extends React.Component<IWorkflowSupportHeaderProps, IWorkflowSupportHeaderState> {
    constructor(props: IWorkflowSupportHeaderProps) {
        super(props);
        this.setReviewer = this.setReviewer.bind(this);
        this.onChange = this.onChange.bind(this);
        this.state = { selectedReviewerIndex: 0, employeeNameMap: new Map<string, string>() };
    }

    async componentDidMount(): Promise<void> {
        const { selectedManagerWorkflows } = this.props.managerWorkflowStore;
        if (!selectedManagerWorkflows || selectedManagerWorkflows.length !== 1) {
            return Promise.resolve();
        }
        const managerWorkflow = selectedManagerWorkflows[0];

        const selectedReviewerIndex = managerWorkflow.currentReviewerIndex;
        this.setState({selectedReviewerIndex, oldWorkflow: managerWorkflow});
        await this.getReviewerNames();
    }

    async componentDidUpdate(): Promise<void> {
        const oldWorkflow = this.state.oldWorkflow;
        const newWorkflows = this.props.managerWorkflowStore.selectedManagerWorkflows;

        const newWorkflow = newWorkflows && newWorkflows[0];

        if (!oldWorkflow && !newWorkflow) {
            return;
        } else if (oldWorkflow && !newWorkflow) {
            await this.updateDropdownState(undefined, 0);
        } else if ((!oldWorkflow && newWorkflow)
            || oldWorkflow?.currentReviewerIndex !== newWorkflow.currentReviewerIndex
            || areDifferent(oldWorkflow.reviewers, newWorkflow.reviewers)) {
            await this.updateDropdownState(newWorkflow, newWorkflow.currentReviewerIndex);
        }
    }

    public render(): React.ReactNode {
        const { selectedManagerWorkflows } = this.props.managerWorkflowStore;
        if (!selectedManagerWorkflows || selectedManagerWorkflows.length !== 1) {
            return null;
        }

        const managerWorkflow = selectedManagerWorkflows[0];

        const { reviewers } = managerWorkflow;

        const options = reviewers
            .map((r, i) => ({
                text: r.reviewerId ? this.state.employeeNameMap.get(r.reviewerId) : '',
                value: i
            }));

        return (
            <div className="workflow-support-header">
                <Select
                    options={options}
                    value={this.state.selectedReviewerIndex}
                    onChange={this.onChange}
                />
                <button
                    onClick={this.setReviewer}
                    className="pxl-btn pxl-btn--sm pxl-btn--primary"
                >
                    Change Current Reviewer
                </button>
            </div>
        );
    }

    private async setReviewer(): Promise<void> {
        const { selectedReviewerIndex } = this.state;
        const { managerWorkflowStore, user } = this.props;
        const { selectedManagerWorkflows } = managerWorkflowStore;
        if (selectedManagerWorkflows.length !== 1) {
            return;
        }
        if (!managerWorkflowStore.managerSourceIds) {
            return;
        }
        const managerWorkflow = selectedManagerWorkflows[0];
        const newReviewer = managerWorkflow.reviewers[selectedReviewerIndex];
        const event = {
            eventType: EventTypes.WorkflowSendBackEvent,
            entityType: EntityTypes.ManagerWorkflow,
            userId: user.userId,
            data: {
                nextReviewer: newReviewer,
                comment: 'Reset by Admin',
                date: new Date(),
                reviewerDisplayName: user.displayName
            }} as IWorkflowEvent;
        await managerWorkflowStore.setManagerWorkflow([{ managerSourceId: managerWorkflow.managerSourceId, event}]);
    }

    private onChange(_: never, selectedValue: string): void {
        this.setState({selectedReviewerIndex: parseInt(selectedValue, 10)});
    }

    private async getReviewerNames(): Promise<void> {
        const { managerWorkflowStore } = this.props;
        const employeeNameMap =
            await managerWorkflowStore.getReviewerNames(managerWorkflowStore.selectedManagerWorkflows[0]);
        this.setState({employeeNameMap});
    }

    private async updateDropdownState(oldWorkflow: ManagerWorkflow|undefined, selectedReviewerIndex: number): Promise<void> {
        this.setState({selectedReviewerIndex, oldWorkflow});
        await this.getReviewerNames();
    }
}

export default observer(WorkflowSupportHeader);